/**
 * ConfirmDialog - Componente de diálogo de confirmação reutilizável
 * 
 * Substitui o window.confirm() nativo por um diálogo moderno e estilizado.
 * Suporta variantes de estilo (danger, warning, info) e estados de loading.
 * 
 * @example
 * <ConfirmDialog
 *   open={showDialog}
 *   onOpenChange={setShowDialog}
 *   variant="danger"
 *   title="Excluir item?"
 *   description="Esta ação não pode ser desfeita."
 *   onConfirm={handleDelete}
 * />
 */

import * as React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Trash2, AlertTriangle, Info, Loader2 } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

// Tipos de variantes disponíveis
export type ConfirmDialogVariant = 'danger' | 'warning' | 'info';

// Interface de props do componente
export interface ConfirmDialogProps {
  /** Controla a visibilidade do diálogo */
  open: boolean;
  /** Callback para mudança de visibilidade */
  onOpenChange: (open: boolean) => void;
  /** Título principal do diálogo */
  title: string;
  /** Descrição opcional abaixo do título */
  description?: string;
  /** Mensagem de aviso destacada (opcional) */
  warningMessage?: string;
  /** Variante visual: danger (vermelho), warning (amarelo), info (azul) */
  variant?: ConfirmDialogVariant;
  /** Ícone customizado (substitui o padrão da variante) */
  icon?: React.ReactNode;
  /** Callback executado ao confirmar */
  onConfirm: () => void | Promise<void>;
  /** Callback executado ao cancelar (opcional) */
  onCancel?: () => void;
  /** Texto do botão de confirmação */
  confirmText?: string;
  /** Texto do botão de cancelamento */
  cancelText?: string;
  /** Estado de loading durante processamento */
  isLoading?: boolean;
  /** Exibir ícone no topo (default: true) */
  showIcon?: boolean;
}

/**
 * Configuração de estilos por variante
 */
/**
 * Configuração de estilos premium por variante
 * Inclui: cores, gradientes, ring e sombras para visual refinado
 */
const variantStyles = {
  danger: {
    iconBg: 'bg-destructive/10',
    iconColor: 'text-destructive',
    iconRing: 'ring-destructive/20',
    gradientClass: 'from-destructive/5 to-transparent',
    buttonClass: 'bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-md',
    DefaultIcon: Trash2,
  },
  warning: {
    iconBg: 'bg-amber-100 dark:bg-amber-900/30',
    iconColor: 'text-amber-600 dark:text-amber-500',
    iconRing: 'ring-amber-500/20',
    gradientClass: 'from-amber-500/5 to-transparent',
    buttonClass: 'bg-amber-500 text-white hover:bg-amber-600 shadow-md',
    DefaultIcon: AlertTriangle,
  },
  info: {
    iconBg: 'bg-primary/10',
    iconColor: 'text-primary',
    iconRing: 'ring-primary/20',
    gradientClass: 'from-primary/5 to-transparent',
    buttonClass: 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-md',
    DefaultIcon: Info,
  },
};

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  warningMessage,
  variant = 'danger',
  icon,
  onConfirm,
  onCancel,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  isLoading = false,
  showIcon = true,
}: ConfirmDialogProps) {
  // Obtém estilos da variante selecionada
  const styles = variantStyles[variant];
  const IconComponent = styles.DefaultIcon;

  // Handler para cancelamento
  const handleCancel = () => {
    onCancel?.();
    onOpenChange(false);
  };

  // Handler para confirmação
  const handleConfirm = async () => {
    await onConfirm();
    // Não fecha automaticamente - deixa o componente pai controlar
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="sm:max-w-[400px] p-0 overflow-hidden">
        {/* Área principal com gradiente sutil da variante */}
        <div className={cn('p-8 bg-gradient-to-b', styles.gradientClass)}>
          <AlertDialogHeader className="flex flex-col items-center text-center gap-4">
            {/* Ícone circular premium com ring */}
            {showIcon && (
              <div
                className={cn(
                  'flex items-center justify-center w-14 h-14 rounded-full ring-4 ring-offset-2 ring-offset-background',
                  styles.iconBg,
                  styles.iconRing
                )}
              >
                {icon || (
                  <IconComponent className={cn('w-6 h-6', styles.iconColor)} />
                )}
              </div>
            )}

            {/* Título premium */}
            <AlertDialogTitle className="text-xl font-semibold tracking-tight leading-tight">
              {title}
            </AlertDialogTitle>

            {/* Descrição e aviso */}
            <div className="space-y-2">
              {description && (
                <AlertDialogDescription className="text-sm text-muted-foreground leading-relaxed">
                  {description}
                </AlertDialogDescription>
              )}

              {/* Mensagem de aviso sutil */}
              {warningMessage && (
                <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground/80">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                  {warningMessage}
                </p>
              )}
            </div>
          </AlertDialogHeader>
        </div>

        {/* Separador visual entre conteúdo e ações */}
        <Separator />

        {/* Footer com botões premium */}
        <AlertDialogFooter className="px-8 py-5 sm:justify-center gap-3">
          {/* Botão Cancelar - borda definida */}
          <AlertDialogCancel
            onClick={handleCancel}
            disabled={isLoading}
            className="sm:min-w-[120px] font-medium text-muted-foreground hover:text-foreground border-border/80 transition-all duration-200 hover:scale-[1.02]"
          >
            {cancelText}
          </AlertDialogCancel>

          {/* Botão Confirmar premium com scale hover */}
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isLoading}
            className={cn(
              'sm:min-w-[120px] font-medium transition-all duration-200 hover:scale-[1.02]',
              styles.buttonClass
            )}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                Aguarde...
              </>
            ) : (
              confirmText
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default ConfirmDialog;
